/**
 * Job List API
 *
 * API client functions for fetching the paginated job list.
 *
 * Endpoint : GET /jobs/job-list
 * Method   : GET
 * Backend  : apps/backend/src/domains/works-lite/jobs/job-list/routes.ts
 */

import { apiClient } from '@/lib/api';
import type { JobListItem, JobListResponse, JobTabCountsResponse } from '@skewb-works/contracts';

export type { JobListItem, JobListResponse, JobTabCountsResponse };

export interface JobListParams {
  [key: string]: unknown;
  limit?: number;
  skip?: number;
  priority_id?: number[];
  job_category_id?: number;
  work_order_status_id?: number;
  work_order_status_name?: string[];
  search_term?: string;
  mir_pending_direction?: 'inbound' | 'outbound' | 'none';
}

/**
 * Fetch a paginated, optionally filtered list of jobs.
 *
 * Filter params are omitted from the request when undefined so that
 * the backend treats them as "no filter" (returns all values).
 */
export async function getJobList(params: JobListParams = {}): Promise<JobListResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('limit', String(params.limit ?? 50));
  searchParams.set('skip', String(params.skip ?? 0));

  if (params.priority_id?.length) searchParams.set('priority_id', params.priority_id.join(','));
  if (params.job_category_id != null)
    searchParams.set('job_category_id', String(params.job_category_id));
  if (params.work_order_status_id != null)
    searchParams.set('work_order_status_id', String(params.work_order_status_id));
  if (params.work_order_status_name != null && params.work_order_status_name.length > 0) {
    for (const name of params.work_order_status_name) {
      searchParams.append('work_order_status_name', name);
    }
  }
  if (params.search_term != null && params.search_term !== '')
    searchParams.set('search_term', params.search_term);
  if (params.mir_pending_direction != null)
    searchParams.set('mir_pending_direction', params.mir_pending_direction);

  return apiClient.get<JobListResponse>(`/jobs/job-list?${searchParams.toString()}`);
}

export async function getJobTabCounts(): Promise<JobTabCountsResponse> {
  return apiClient.get<JobTabCountsResponse>('/jobs/tab-counts');
}
